import { useState, useCallback, useEffect, useMemo } from "react"
import type { Table } from "@tanstack/react-table"
import { REPORT_CONFIGS } from "@/types/report"
import type { ReportFilters, GridState, ViewMode } from "@/types/report"
import { useReportData } from "@/hooks/use-report-data"
import { useViewState } from "@/hooks/use-view-state"
import { useReportExport } from "@/hooks/use-report-export"
import { DataGrid } from "@/components/data-grid/data-grid"
import { FilterBar } from "@/components/data-grid/filter-bar"
import { ReportToolbar } from "@/components/report/report-toolbar"
import { SaveViewDialog } from "@/components/views/save-view-dialog"
import { ShareViewDialog } from "@/components/views/share-view-dialog"
import { EngagementGantt } from "@/components/gantt/engagement-gantt"
import { PeopleGantt } from "@/components/gantt/people-gantt"
import { EngagementKanban } from "@/components/kanban/engagement-kanban"
import { PeopleDashboard } from "@/components/dashboard/people-dashboard"
import { TimeEntriesDashboard } from "@/components/dashboard/time-entries-dashboard"
import { columnRegistry } from "@/lib/report-configs"
import { FileX } from "lucide-react"
import { toast } from "sonner"

interface ReportPageProps {
  reportCode: string
}

const emptyGridState: GridState = {
  columnVisibility: {},
  columnOrder: [],
  columnSizing: {},
  sorting: [],
  columnFilters: [],
  globalFilter: "",
  pagination: { pageSize: 50 },
  grouping: [],
}

export default function ReportPage({ reportCode }: ReportPageProps) {
  const config = REPORT_CONFIGS.find((r) => r.code === reportCode)
  const componentName = `sp-${reportCode}`
  const reportColumns = columnRegistry[reportCode] ?? columnRegistry["engagement-finance"]

  const filters = useMemo<ReportFilters>(() => {
    if (reportCode === "engagement-finance") {
      const today = new Date()
      return {
        effectiveFrom: new Date(today.getFullYear(), 0, 1).toISOString().split("T")[0],
        effectiveTo: today.toISOString().split("T")[0],
      }
    }
    return {}
  }, [reportCode])
  const [globalFilter, setGlobalFilter] = useState("")
  const [showFilters, setShowFilters] = useState(true)
  const [viewMode, setViewMode] = useState<ViewMode>("table")
  useEffect(() => { setViewMode("table") }, [reportCode])
  const availableViews = useMemo<ViewMode[]>(() => {
    if (reportCode === "engagement-finance") return ["table", "gantt", "kanban"]
    if (reportCode === "engagement-people") return ["table", "gantt", "dashboard"]
    if (reportCode === "engagement-time-entries") return ["table", "dashboard"]
    return ["table"]
  }, [reportCode])
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [tableInstance, setTableInstance] = useState<Table<any> | null>(null)
  const [appliedViewState, setAppliedViewState] = useState<GridState | null>(null)
  const [filteredData, setFilteredData] = useState<any[] | null>(null)

  const handleFilteredDataChange = useCallback((data: any[]) => {
    setFilteredData(data)
  }, [])

  const peopleWorkerIdFilter = useMemo<Set<number> | null>(() => {
    if (reportCode !== "engagement-people" || filteredData == null) return null
    return new Set(filteredData.map((r: any) => r.userProfileId).filter((id): id is number => typeof id === "number"))
  }, [reportCode, filteredData])

  const { data, isLoading, error, refresh } = useReportData(reportCode, filters)

  const {
    views,
    activeViewId,
    setActiveViewId,
    loadViewState,
    saveView,
    deleteView,
    autoSaveSessionState,
    restoreSessionState,
  } = useViewState(componentName)

  const { exportToExcel } = useReportExport(tableInstance, config?.label ?? "Report")

  const activeView = views.find((v) => v.userProfileSearchStateId === activeViewId)

  // Helper to get current grid state from table instance
  const getCurrentGridState = useCallback((): GridState => {
    if (!tableInstance) return emptyGridState
    const state = tableInstance.getState()
    return {
      columnVisibility: state.columnVisibility,
      columnOrder: state.columnOrder,
      columnSizing: state.columnSizing,
      sorting: state.sorting,
      columnFilters: state.columnFilters,
      globalFilter: state.globalFilter ?? "",
      pagination: { pageSize: state.pagination.pageSize },
      grouping: state.grouping,
    }
  }, [tableInstance])

  // Restore session state on mount
  useEffect(() => {
    restoreSessionState().then((state) => {
      if (state) {
        setGlobalFilter(state.globalFilter ?? "")
        setAppliedViewState(state)
      }
    }).catch((err) => {
      console.error("Failed to restore session state:", err)
    })
  }, [restoreSessionState])

  // Auto-save session state when grid state changes
  const tableState = tableInstance?.getState()
  const tableStateKey = tableState
    ? JSON.stringify({
        s: tableState.sorting,
        f: tableState.columnFilters,
        v: tableState.columnVisibility,
        o: tableState.columnOrder,
        z: tableState.columnSizing,
        g: tableState.globalFilter,
        p: tableState.pagination.pageSize,
        gr: tableState.grouping,
      })
    : null

  useEffect(() => {
    if (!tableInstance || !tableStateKey) return
    autoSaveSessionState(getCurrentGridState())
  }, [tableStateKey, tableInstance, getCurrentGridState, autoSaveSessionState])

  const handleSelectView = useCallback(async (stateId: number | null) => {
    if (stateId) {
      try {
        const state = await loadViewState(stateId)
        if (!state) {
          toast.error("Failed to load view")
          return
        }
        setActiveViewId(stateId)
        setGlobalFilter(state.globalFilter ?? "")
        setAppliedViewState(state)
        toast.success("View loaded")
      } catch {
        toast.error("Failed to load view")
      }
    } else {
      setActiveViewId(null)
      setGlobalFilter("")
      setAppliedViewState({
        columnVisibility: reportColumns.defaultHiddenColumns,
        columnOrder: [],
        columnSizing: {},
        sorting: [],
        columnFilters: [],
        globalFilter: "",
        pagination: { pageSize: 50 },
        grouping: [],
      })
    }
  }, [loadViewState, setActiveViewId, reportColumns.defaultHiddenColumns])

  const handleSaveView = useCallback(async (name: string, description: string, existingStateId?: number) => {
    const result = await saveView({
      stateName: name,
      stateDescription: description,
      state: getCurrentGridState(),
      existingStateId,
    })
    if (result) {
      setActiveViewId(result)
      toast.success("View saved")
    }
  }, [saveView, activeViewId, setActiveViewId, getCurrentGridState])

  const handleShare = useCallback(async (isPublic: boolean, userProfileIds: number[], teamGuids: string[]) => {
    if (!activeViewId) { toast.error("Save a view first"); return }
    const result = await saveView({
      stateName: activeView?.stateName ?? "Shared View",
      stateDescription: activeView?.stateDescription ?? "",
      state: getCurrentGridState(),
      isPublic, userProfileIds, teamGuids,
      existingStateId: activeViewId,
    })
    if (result) {
      toast.success("Share settings updated")
    } else {
      toast.error("Failed to update share settings")
    }
  }, [activeViewId, activeView, saveView, getCurrentGridState])

  const handleDeleteView = useCallback(async (stateId: number) => {
    try {
      await deleteView(stateId)
      if (activeViewId === stateId) {
        setActiveViewId(null)
        setGlobalFilter("")
        setAppliedViewState({
          columnVisibility: reportColumns.defaultHiddenColumns,
          columnOrder: [],
          columnSizing: {},
          sorting: [],
          columnFilters: [],
          globalFilter: "",
          pagination: { pageSize: 50 },
          grouping: [],
        })
      }
      toast.success("View deleted")
    } catch {
      toast.error("Failed to delete view")
    }
  }, [deleteView, activeViewId, setActiveViewId, reportColumns.defaultHiddenColumns])

  const handleReset = useCallback(() => {
    setGlobalFilter("")
    setActiveViewId(null)
    setAppliedViewState({
      columnVisibility: reportColumns.defaultHiddenColumns,
      columnOrder: [],
      columnSizing: {},
      sorting: [],
      columnFilters: [],
      globalFilter: "",
      pagination: { pageSize: 50 },
      grouping: [],
    })
    toast.info("Reset to default view")
  }, [setActiveViewId, reportColumns.defaultHiddenColumns])

  if (!config) return <div className="flex items-center justify-center h-full text-slate-500">Report not found</div>

  if (!config.enabled) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-500">
        <FileX className="h-12 w-12 text-slate-300" />
        <p className="text-lg font-semibold">Coming Soon</p>
        <p className="text-sm">The {config.label} report is not yet available.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <ReportToolbar
        onRefresh={refresh}
        onShare={() => setShowShareDialog(true)}
        onReset={handleReset}
        onSaveView={() => setShowSaveDialog(true)}
        onExport={exportToExcel}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filtersVisible={showFilters}
        views={views}
        activeViewId={activeViewId}
        onSelectView={handleSelectView}
        onDeleteView={handleDeleteView}
        canShare={!!activeViewId}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        availableViews={availableViews}
        activeFilterCount={tableInstance ? tableInstance.getState().columnFilters.length + (tableInstance.getState().globalFilter ? 1 : 0) : 0}
      />

      <div className="flex-1 overflow-hidden p-4">
        {/* DataGrid is always mounted so the TanStack Table filter engine stays alive.
            Hidden via CSS when a non-table view is active. */}
        <div style={{ display: viewMode === "table" ? undefined : "none" }} className="h-full">
          <DataGrid
            data={data}
            columns={reportColumns.columns}
            isLoading={isLoading}
            error={error}
            globalFilter={globalFilter}
            onGlobalFilterChange={setGlobalFilter}
            showFilters={showFilters}
            tableRef={setTableInstance}
            defaultHiddenColumns={reportColumns.defaultHiddenColumns}
            rollUpColumnId={reportColumns.rollUpColumnId}
            appliedViewState={appliedViewState}
            onFilteredDataChange={handleFilteredDataChange}
          />
        </div>
        {viewMode !== "table" && (
          <div className="flex flex-col h-full">
            {showFilters && (
              <FilterBar
                table={tableInstance}
                globalFilter={globalFilter}
                onGlobalFilterChange={setGlobalFilter}
              />
            )}
            <div className="flex-1 overflow-hidden">
              {isLoading && (
                <div className="flex items-center justify-center h-full text-sm text-slate-500" role="status" aria-live="polite">Loading...</div>
              )}
              {error && (
                <div className="flex items-center justify-center h-full text-sm text-red-600" role="alert" aria-live="assertive">{error}</div>
              )}
              {viewMode === "gantt" && reportCode === "engagement-finance" && !isLoading && !error && (
                <EngagementGantt data={(filteredData ?? data) as any} />
              )}
              {viewMode === "gantt" && reportCode === "engagement-people" && !isLoading && !error && (
                <PeopleGantt workerIdFilter={peopleWorkerIdFilter} />
              )}
              {viewMode === "kanban" && reportCode === "engagement-finance" && !isLoading && !error && (
                <EngagementKanban data={(filteredData ?? data) as any} />
              )}
              {viewMode === "dashboard" && reportCode === "engagement-people" && !isLoading && !error && (
                <PeopleDashboard data={(filteredData ?? data) as any} />
              )}
              {viewMode === "dashboard" && reportCode === "engagement-time-entries" && !isLoading && !error && (
                <TimeEntriesDashboard data={(filteredData ?? data) as any} />
              )}
            </div>
          </div>
        )}
      </div>

      <SaveViewDialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        onSave={handleSaveView}
        views={views}
        activeViewId={activeViewId}
      />

      <ShareViewDialog
        open={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        onSave={handleShare}
        isPublic={activeView?.isPublic ?? false}
        shareeUserIds={activeView?.userProfileIds ?? []}
        shareeTeamIds={activeView?.teamGuids ?? []}
        isOwner={true}
      />
    </div>
  )
}
